import { Link } from 'react-router-dom';
import { ArrowUpRight, Phone } from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';
import { Reveal } from '../components/Motion';
import { COMPANY, IMG } from '../data/site';

export default function APropos() {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-16 overflow-hidden">
        <AnimatedBackground />
        <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="overline mb-4">◍ À propos</div>
            <h1 className="font-display italic text-[13vw] sm:text-[10vw] lg:text-[8vw] leading-[0.9]">
              L’<em className="not-italic underline decoration-white/40 underline-offset-8">équipe</em> Ponthieu.
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-2xl font-display italic text-3xl md:text-4xl leading-tight text-white/80">
              solution technique au quotidien.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Intro split */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-12 gap-8 md:gap-12">
          <Reveal className="col-span-12 md:col-span-6">
            <div className="hero-media relative aspect-[4/5] overflow-hidden rounded-2xl bg-black border border-white/10" style={{ boxShadow: 'var(--shadow-elev)' }}>
              <img src="/apropos-hero.png" alt="Équipe Ponthieu — serrurerie, métallerie, vitrerie, dépannage" className="w-full h-full object-cover opacity-95" style={{ filter: 'grayscale(8%) contrast(1.05)' }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <div className="absolute inset-4 border border-white/10 rounded-xl pointer-events-none" />
              <div className="absolute right-4 top-4 glass px-3 py-1 font-mono text-[10px] tracking-[0.22em] uppercase text-white/85">Ponthieu</div>
            </div>
          </Reveal>
          <div className="col-span-12 md:col-span-6">
            <Reveal delay={0.1}>
              <div className="overline mb-4">◍ Notre entreprise</div>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05]">
                Une entreprise reconnue pour son savoir-faire et son exigence.
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed">
                Depuis plusieurs années, Ponthieu Pose et Rénovation accompagne les particuliers,
                les professionnels et les copropriétés dans leurs projets avec une exigence
                constante de qualité. Chaque intervention est réalisée avec méthode, précision
                et le souci du détail, afin de garantir des réalisations durables et conformes
                aux attentes de nos clients.
              </p>
              <p className="mt-4 text-white/60 leading-relaxed">
                Notre engagement repose sur des matériaux de qualité, une exécution soignée
                et un accompagnement personnalisé à chaque étape du projet. La satisfaction
                de nos clients demeure au cœur de chacune de nos réalisations.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Notre exigence pillars */}
      <section className="relative py-24 md:py-32 border-y border-white/10 bg-ink-900">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="overline mb-4">◍ Manifeste</div>
            <h2 className="font-display italic text-5xl md:text-6xl leading-[0.95] max-w-3xl">
              Notre exigence : un travail fiable, précis et durable.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {[
              ['Savoir-faire', "Serrurerie, métallerie, vitrerie et dépannage exécutés au sein du même atelier."],
              ['Sur-mesure', "Chaque ouvrage est dessiné, calculé et réalisé pour son lieu — sans compromis."],
              ['Relation client', "Un interlocuteur dédié, une écoute réelle et un accompagnement complet."],
              ['Matériaux', "Aciers, quincailleries et vitrages sélectionnés pour leur qualité et leur durabilité."],
              ['Qualité des finitions', "Ajustage, ponçage, thermolaquage : la finesse d’exécution est notre signature."],
              ['Paris et Île-de-France', "Nous intervenons dans toute la région parisienne pour accompagner les particuliers, les professionnels et les copropriétés avec la même exigence de qualité, de réactivité et de précision."],
            ].map(([t, d]) => (
              <div key={t} className="bg-ink-900 p-8 md:p-10">
                <h3 className="font-display italic text-2xl md:text-3xl leading-tight">{t}</h3>
                <p className="mt-4 text-white/60 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden">
        <AnimatedBackground />
        <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10 flex flex-wrap gap-6 items-end justify-between">
          <h2 className="font-display italic text-5xl md:text-6xl leading-[0.95] max-w-2xl">
            Travaillons <em className="not-italic underline decoration-white/40 underline-offset-4">ensemble</em>.
          </h2>
          <div className="flex gap-3">
            <a href={`tel:${COMPANY.phoneTel}`} className="shine-wrap inline-flex items-center gap-2 border border-white/25 px-5 h-12 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-white/10"><Phone size={14} strokeWidth={1.5} /> {COMPANY.phone}</a>
            <Link to="/contact" className="shine-wrap inline-flex items-center gap-2 bg-white text-black px-5 h-12 font-mono text-[11px] tracking-[0.22em] uppercase">Devis <ArrowUpRight size={14} strokeWidth={1.5} /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
